import moment from 'moment'
import React, { useState } from 'react'

import { EuiText } from '@elastic/eui'

import { MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'

import { getEndDate } from '../../../../utils/CampaignMathUtils'
import CampaignRow from './components/CampaignRow'
import Legend from './components/Legend'
import MonthHeader from './components/MonthHeader'
import { typeColors, offAdCritterPlatformColors, generateMonthsData, organizeCampaignsIntoRows } from './utils'

export interface GanttChartProps {
  campaigns: MasterCampaignPlanCampaignBuilder[]
  planStartDate: moment.Moment
  planDurationMonths: number
  ganttChartRef?: React.RefObject<HTMLDivElement>
  isClickable?: boolean
  isHoverable?: boolean
  presorted?: boolean
}

const GanttChart: React.FC<GanttChartProps> = ({ campaigns, planStartDate, planDurationMonths, ganttChartRef, isClickable = false, isHoverable = false, presorted = false }) => {
  const [hoveredType, setHoveredType] = useState<string | null>(null)

  const getMinDateFromCampaigns = () => {
    if (campaigns.length === 0) {
      return planStartDate.clone().startOf('month')
    }

    let minStartDate = planStartDate.clone().startOf('month')
    for (const campaign of campaigns) {
      const startDate = moment(campaign.startDate)
      if (startDate.isBefore(minStartDate)) {
        minStartDate = startDate
      }
    }

    return minStartDate.clone().startOf('month')
  }

  const minDate = getMinDateFromCampaigns()

  const getMaxDateFromCampaigns = () => {
    if (campaigns.length === 0) {
      return minDate
    }

    let maxEndDate = planStartDate.clone().add(planDurationMonths, 'months').endOf('month')
    for (const campaign of campaigns) {
      const endDate = getEndDate(campaign)
      if (!endDate) {
        continue
      }

      if (endDate.isAfter(maxEndDate)) {
        maxEndDate = endDate
      }
    }

    return maxEndDate.add(planDurationMonths + 1, 'months').endOf('month')
  }
  const maxDate = getMaxDateFromCampaigns()
  const totalDays = maxDate.diff(minDate, 'days')
  const totalMonthsToShow = planDurationMonths + 1

  const months = generateMonthsData(planStartDate, planDurationMonths, minDate, totalDays)

  if (campaigns.length === 0) {
    return (
      <EuiText>
        <p>No campaigns available to display in the Gantt chart.</p>
      </EuiText>
    )
  }

  const rows = organizeCampaignsIntoRows(campaigns, presorted)

  const campaignTypeOrder = ['TV', 'Billboard', 'Audio', 'Internet', 'Google', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok']
  const legendItems: Record<string, string> = {}
  const customOptionsUsed = new Map<string, string>()

  campaigns.forEach(campaign => {
    if (campaign.isOffAdCritter && campaign.offAdCritterName) {
      const color = offAdCritterPlatformColors[campaign.offAdCritterName] || campaign.offAdCritterColor || typeColors.OffAdCritter || '#999999'
      if (!campaignTypeOrder.includes(campaign.offAdCritterName)) {
        customOptionsUsed.set(campaign.offAdCritterName, color)
      } else {
        legendItems[campaign.offAdCritterName] = color
      }
    } else {
      const color = typeColors[campaign.campaignType]
      if (color) {
        legendItems[campaign.campaignType] = color
      }
    }
  })

  const orderedLegendItems: Record<string, string> = {}
  campaignTypeOrder.forEach(type => {
    if (legendItems[type]) {
      orderedLegendItems[type] = legendItems[type]
    }
  })

  customOptionsUsed.forEach((color, name) => {
    orderedLegendItems[name] = color
  })

  return (
    <div style={{ position: 'relative' }}>
      <Legend legendItems={orderedLegendItems} hoveredType={isHoverable ? hoveredType : null} onHoverChange={isHoverable ? setHoveredType : () => {}} isHoverable={isHoverable} />

      <div style={{ overflowX: 'auto', width: '100%', marginTop: '50px' }}>
        <div style={{ position: 'relative', width: '100%', minWidth: '920px' }}>
          <MonthHeader months={months} />

          <div style={{ width: '100%', position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                pointerEvents: 'none',
                zIndex: 1
              }}>
              {months.map((month, index) => (
                <div
                  key={`grid-${index}`}
                  style={{
                    flex: '1',
                    borderLeft: index > 0 ? '1px solid #e8eaed' : 'none',
                    height: '100%'
                  }}
                />
              ))}
            </div>

            <div style={{ width: '100%', position: 'relative', zIndex: 2 }}>
              {rows.map((campaignsInRow, rowIndex) => (
                <CampaignRow key={rowIndex} campaigns={campaignsInRow} rowIndex={rowIndex} minDate={minDate} totalMonthsToShow={totalMonthsToShow} ganttChartRef={ganttChartRef} hoveredType={isHoverable ? hoveredType : null} isClickable={isClickable} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GanttChart
